import { useState } from "react";
import { useDeliveries, useDelivery, useDeliverRun, useRetryDelivery, useRun } from "../../api/hooks";
import type { DeliveryRecord } from "../../api/types";
import { useSession } from "../../app/store";
import { JsonTree, Spinner, StatusDot, errText, fmtDate } from "../../components/ui";
import "./platform.css";

const STATUSES: DeliveryRecord["status"][] = ["queued", "delivering", "delivered", "dead"];

/** Dock tab: delivery history for the current project (or current run), with manual retry. */
export function DeliveriesPanel() {
  const { projectId, currentRunId, toast } = useSession();
  const [scope, setScope] = useState<"run" | "project">(currentRunId ? "run" : "project");
  const [status, setStatus] = useState<string>("");
  const [open, setOpen] = useState<string | null>(null);
  const run = useRun(scope === "run" ? currentRunId : null);
  const list = useDeliveries(
    { project: projectId, run: scope === "run" ? currentRunId : null, status: status || null, limit: 200 },
    !!projectId,
  );
  const retry = useRetryDelivery();
  const deliverRun = useDeliverRun();
  const counts = list.data?.counts ?? {};

  const redeliver = async () => {
    if (!currentRunId) return;
    try {
      const r = await deliverRun.mutateAsync({ runId: currentRunId });
      toast(`${r.queued.length} delivery(ies) queued${r.skipped.length ? `, ${r.skipped.length} skipped` : ""}`, r.queued.length ? "success" : "info");
    } catch (e) {
      toast(errText(e), "error");
    }
  };

  if (!projectId) return <span className="dim">Select a project.</span>;
  return (
    <div className="deliveries">
      <div className="row wrap deliveries-bar">
        <div className="tabs inline">
          <button className={scope === "project" ? "on" : ""} onClick={() => setScope("project")}>
            project
          </button>
          <button className={scope === "run" ? "on" : ""} onClick={() => setScope("run")} disabled={!currentRunId} title={currentRunId ?? "no current run"}>
            current run
          </button>
        </div>
        {STATUSES.map((s) => (
          <button key={s} className={`chip${status === s ? " on" : ""}`} onClick={() => setStatus(status === s ? "" : s)}>
            <StatusDot status={s} /> {s} <span className="faint">{counts[s] ?? 0}</span>
          </button>
        ))}
        <span className="spacer" />
        {list.isFetching ? <Spinner /> : null}
        {scope === "run" && run.data?.status === "done" ? (
          <button className="btn sm" onClick={redeliver} disabled={deliverRun.isPending} title="Route this run's artifacts through subscriptions.yaml again">
            Deliver run again
          </button>
        ) : null}
        <button className="btn ghost sm" onClick={() => list.refetch()} title="refresh">
          ↻
        </button>
      </div>
      {list.isError ? <div className="problem err">{errText(list.error)}</div> : null}
      {list.data && !list.data.deliveries.length ? (
        <span className="dim small">
          No deliveries{status ? ` with status ${status}` : ""}. Runs started with "deliver" enabled queue one delivery per matching (artifact, subscription, sink).
        </span>
      ) : null}
      {list.data?.deliveries.length ? (
        <table className="tbl deliveries-tbl">
          <thead>
            <tr>
              <th />
              <th>When</th>
              <th>Artifact</th>
              <th>Subscription → sink</th>
              <th>Attempts</th>
              <th>Result</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {list.data.deliveries.map((d) => (
              <tr key={d.id} className={`clickable${open === d.id ? " sel" : ""}`} onClick={() => setOpen(open === d.id ? null : d.id)}>
                <td>
                  <StatusDot status={d.status} />
                </td>
                <td className="small dim" title={d.id}>
                  {fmtDate(d.delivered_at ?? d.updated_at)}
                </td>
                <td>
                  <span className="mono">{d.artifact_filename}</span> <span className="faint small">{d.report}</span>
                  {d.flight_id ? <span className="faint small"> · {d.flight_id}</span> : null}
                </td>
                <td>
                  <span className="mono">{d.subscription_id}</span> → <span className="mono">{d.sink_id}</span> <span className="badge">{d.sink_kind}</span>
                </td>
                <td className="num">
                  {d.attempt}/{d.max_attempts}
                  {d.status === "queued" && d.next_attempt_at && d.attempt > 0 ? <div className="faint small">retry {fmtDate(d.next_attempt_at)}</div> : null}
                </td>
                <td className={`small${d.status === "dead" ? " sev-error" : ""}`} style={{ maxWidth: 380, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={d.error ?? d.location ?? ""}>
                  {d.status === "delivered" ? d.location ?? "delivered" : d.error ?? d.status}
                </td>
                <td>
                  {d.status === "dead" || (d.status === "queued" && d.attempt > 0) ? (
                    <button
                      className="btn sm"
                      disabled={retry.isPending}
                      onClick={async (e) => {
                        e.stopPropagation();
                        try {
                          await retry.mutateAsync(d.id);
                          toast("retry queued");
                        } catch (err) {
                          toast(errText(err), "error");
                        }
                      }}
                    >
                      Retry now
                    </button>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : null}
      {open ? <DeliveryDetail id={open} onClose={() => setOpen(null)} /> : null}
    </div>
  );
}

function DeliveryDetail({ id, onClose }: { id: string; onClose: () => void }) {
  const d = useDelivery(id);
  if (!d.data) return <Spinner label="loading delivery" />;
  const row = d.data;
  return (
    <div className="delivery-detail">
      <div className="row">
        <StatusDot status={row.status} />
        <span className="mono">{row.id}</span>
        <span className="badge">{row.status}</span>
        <span className="dim small">
          created {fmtDate(row.created_at)}
          {row.delivered_at ? ` · delivered ${fmtDate(row.delivered_at)}` : ""}
        </span>
        <span className="spacer" />
        <button className="btn ghost sm" onClick={onClose}>
          ✕
        </button>
      </div>
      <div className="delivery-cols">
        <div>
          <div className="section-title">Attempts ({row.attempts.length})</div>
          {row.attempts.length ? (
            <table className="tbl">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Started</th>
                  <th>ms</th>
                  <th>Result</th>
                </tr>
              </thead>
              <tbody>
                {row.attempts.map((a, i) => (
                  <tr key={i}>
                    <td className="num">{a.attempt}</td>
                    <td className="small dim">{fmtDate(a.started_at)}</td>
                    <td className="num">{a.elapsed_ms}</td>
                    <td className={`small${a.ok ? "" : " sev-error"}`}>
                      {a.ok ? "✓ " : a.retryable ? "↻ " : "✕ "}
                      {a.detail}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <span className="dim small">not attempted yet</span>
          )}
        </div>
        <div>
          <div className="section-title">Context</div>
          <JsonTree value={row.context ?? {}} />
        </div>
      </div>
    </div>
  );
}
